import { z } from 'zod';
import { ScannerToolEvidenceSchema } from '../../../../contracts/evidence/scanner-tool-evidence';

/**
 * Per-tool outcome for one static-analysis run. Mirrors `ToolRunStatus` in
 * `engine/scanners/static-analysis-types.ts`; `static-analysis.run.test.ts`
 * asserts the two stay in step.
 *
 * Lives in its own module rather than in `graph.ts` to keep that file inside
 * its `max-lines` budget.
 */
export const ToolRunStatusSchema = z.object({
  evidence: ScannerToolEvidenceSchema.optional(),
  name: z.string(),
  status: z.enum(['ok', 'skipped', 'failed']),
  findingCount: z.number(),
  /**
   * The tool's own version, when the scan could determine it.
   *
   * Declared here because it TRAVELS: the capture records it, a comparison
   * treats a change in it as drift, and the upload digest has to be able to
   * see it — two runs whose only difference is a tool version are not the
   * same analysis, and a digest blind to it called them equal.
   */
  version: z.string().optional(),
});

/**
 * Notes for `CachedScanSchema.saToolStatuses`, which is declared in `graph.ts`
 * (at its `max-lines` budget, so the prose lives here):
 *
 * Absent means static analysis was not attempted for that scan. An array whose
 * entries are all `skipped` is the different, positive claim that the suite ran
 * and nothing was installed. Optional so scans cached before the field existed
 * still parse.
 */

/**
 * Notes for `CachedScanSchema.saRelationalFindings`, declared in `graph.ts`
 * (which sits at its `max-lines` budget, so the prose lives here):
 *
 * Grouped and ranked results, one entry per tool that produces them. Absent
 * when static analysis was not attempted, or when it produced none. Persisted
 * on the cached scan rather than as graph meta because `MetaSchema` takes flat
 * scalars and a clone family is not one — the same constraint that put
 * `saToolStatuses` here.
 *
 * Relational static-analysis output (#2596) as persisted on a cached scan.
 *
 * Mirrors `RelationalFindings` in `engine/scanners/static-analysis-types.ts`.
 * Optional fields are optional here too: absent means the tool did not report
 * the value, which is not the same as reporting zero, and the schema must not
 * quietly turn one into the other on the way through storage.
 *
 * Clone source text is deliberately absent — fallow emits it, and scan
 * artefacts cross a tenancy boundary. See `CloneFamily`.
 */
const CloneInstanceSchema = z.object({
  filePath: z.string(),
  startLine: z.number(),
  endLine: z.number(),
});

export const CloneFamilySchema = z.object({
  filePaths: z.array(z.string()),
  groups: z.array(z.array(CloneInstanceSchema)),
});

export const DuplicationStatsSchema = z.object({
  totalFiles: z.number().optional(),
  filesWithClones: z.number().optional(),
  totalLines: z.number().optional(),
  duplicatedLines: z.number().optional(),
  duplicationPercentage: z.number().optional(),
  cloneGroupCount: z.number().optional(),
});

export const RefactorTargetSchema = z.object({
  filePath: z.string(),
  priority: z.number(),
  category: z.string().optional(),
  recommendation: z.string().optional(),
  effort: z.enum(['low', 'medium', 'high']).optional(),
  confidence: z.enum(['low', 'medium', 'high']).optional(),
  factors: z.array(
    z.object({
      metric: z.string(),
      value: z.number(),
      threshold: z.number(),
      detail: z.string().optional(),
    }),
  ),
});

export const FileScoreSchema = z.object({
  filePath: z.string(),
  fanIn: z.number().optional(),
  fanOut: z.number().optional(),
  deadCodeRatio: z.number().optional(),
  complexityDensity: z.number().optional(),
  maintainabilityIndex: z.number().optional(),
  crapMax: z.number().optional(),
  lines: z.number().optional(),
});

export const RelationalFindingsSchema = z.object({
  tool: z.string(),
  /** The tool's version and the output schema this was mapped against, so a
   *  stored result stays interpretable after either moves on. */
  toolVersion: z.string().optional(),
  schemaVersion: z.number().optional(),
  cloneFamilies: z.array(CloneFamilySchema),
  duplication: DuplicationStatsSchema.optional(),
  refactorTargets: z.array(RefactorTargetSchema),
  fileScores: z.array(FileScoreSchema),
});
